import Breadcrumb from "../Breadcrumb";
import Percentage from "../../../assets/used-images/percentage.png";
import OfferBox from "./components/offers/OfferBox";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Countdown from "react-countdown";
import axios from "axios";
import { useLanguage } from "../../../store/language-context";
import { useTranslation } from "react-i18next";

const OffersDetails = () => {
  const { URL } = useLanguage();
  const { id } = useParams();
  const { t } = useTranslation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['offers-details', id, URL],
    queryFn: async () => {
      try {
        const response = await axios.get(`${URL}/offers/${id}`);
        return response?.data;
      } catch (error) {
        throw new Error(t('custom.err'))
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <>
        <div className="h-12 w-full l-load-dt"></div>
        <div className="w-full px-20 py-4 max-lg:px-4">
          <div className="w-full rounded-md l-load-dt h-32 max-lg:h-80"></div>
          <div className="grid grid-cols-3 gap-2 max-xl:grid-cols-2 max-lg:grid-cols-1 relative">
            {
              [...Array(6)].map((_, i) => (
                <div className='relative pt-7 pb-3 px-3 grid grid-cols-[28%_72%] border rounded-lg w-[27rem] max-lg:w-full max-lg:grid-cols-1 mb-5 mt-5' key={i}>
                  {/* Image Placeholder */}
                  <div className='relative w-32 max-lg:h-18'>
                    <div className='w-[7.5rem] h-[7.5rem] flex justify-center items-center absolute -top-14 -right-5 max-md:relative max-lg:h-28 max-lg:w-48 max-lg:relative'>
                      <div className='l-load-dt w-[7.8rem] h-[7.8rem] max-lg:h-[7.4rem] max-lg:w-[12.4rem] absolute z-0 rounded-lg'></div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className='flex flex-col justify-between gap-4'>
                    <div className='!text-start space-y-3'>
                      <div className='h-5 l-load-dt rounded w-3/4 ml-auto'></div>

                      <div className='flex items-center gap-2'>
                        <div className='l-load-dt rounded-full w-4 h-4'></div>
                        <div className='h-4 l-load-dt rounded w-1/3'></div>
                      </div>

                      <div className='flex items-center gap-2'>
                        <div className='l-load-dt rounded-full w-4 h-4'></div>
                        <div className='h-4 l-load-dt rounded w-1/2'></div>
                      </div>

                      <div className='flex items-center gap-2'>
                        <div className='l-load-dt rounded-full w-4 h-4'></div>
                        <div className='h-4 l-load-dt rounded w-1/3'></div>
                      </div>
                    </div>

                    <div className='flex items-center gap-2 mt-2 flex-wrap max-lg:items-start'>
                      <div className='h-4 w-16 l-load-dt rounded'></div>
                      <div className='h-6 w-36 l-load-dt rounded'></div>
                    </div>
                  </div>

                  {/* Button */}
                  <div className="flex items-center justify-between gap-2 mt-3 text-nowrap w-full col-span-2">
                    <div className="h-9 l-load-dt rounded-lg w-40"></div>
                  </div>
                </div>
              ))
            }

          </div>
        </div>
      </>
    );
  }

  if (error) {
    return <div id="err">{error?.message || t('custom.no-data-page')}</div>;
  }

  if (data && data.status === 200) {
    const { end_date, course_offer = [] } = data.data;

    const countdownEndDate = new Date(end_date).getTime();

    return (
      <>
        <Breadcrumb pages={[t('routes.offers')]} />
        <main className="container">
          <section
            className="grid grid-cols-3 max-xl:grid-cols-2 max-md:grid-cols-1 max-lg:gap-8 px-4 py-6 rounded-lg bg-[linear-gradient(180deg,#6A2F38_0%,#440D1D_100%)] sticky top-0 z-50 mt-4 mb-4"
          >
            <div className="flex flex-col justify-center !text-start text-white space-y-2 max-lg:text-center">
              <p className="text-3xl text-end">
                {t('offers.title')}
              </p>
              <article className="text-secondary-tw" dangerouslySetInnerHTML={{ __html: t('offers.desc') }} />
            </div>

            <div className="flex justify-end items-center">
              <div className="flex justify-center items-start gap-3 text-white flex-wrap">
                <Countdown
                  date={countdownEndDate}
                  renderer={({ days, hours, minutes, seconds }) => (
                    <div className="time-display flex-wrap">
                      <div className="time">
                        <span>{String(days).padStart(2, '0')}</span>
                        <span>{t('offers.day')}</span>
                      </div>
                      <b>:</b>
                      <div className="time">
                        <span>{String(hours).padStart(2, '0')}</span>
                        <span>{t('offers.hour')}</span>
                      </div>
                      <b>:</b>
                      <div className="time">
                        <span>{String(minutes).padStart(2, '0')}</span>
                        <span>{t('offers.minute')}</span>
                      </div>
                      <b>:</b>
                      <div className="time">
                        <span>{String(seconds).padStart(2, '0')}</span>
                        <span>{t('offers.second')}</span>
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end max-lg:justify-center">
              <img src={Percentage} alt="offer" className="h-32" />
            </div>
          </section>

          <section className="grid lg:grid-cols-3 gap-16 relative">
            {error || course_offer.length === 0 ? (
              <div className=" top-10 z-50 bg-white flex justify-center items-center h-full w-full text-xl">
                <p id="err">{t('custom.no-data')}</p>
              </div>
            ) : (
              course_offer.map((courseOffer, i) => (
                <OfferBox 
                  offer={{
                    ...courseOffer,
                    parent_offer_start_date: data.data.start_date,
                    parent_offer_end_date: data.data.end_date
                  }} 
                  key={courseOffer?.id || i} />
              ))
            )}
          </section>
        </main>

      </>
    );
  }
};

export default OffersDetails;
